import React, { useMemo, useState } from 'react';
import { CreditCard as CreditCardIcon } from 'lucide-react';
import { Modal } from '@components/Dialog';
import { Button } from '@components/Button';
import { ColorPicker } from '@components/ColorPicker';
import { MoneyInput } from '@components/MoneyInput';
import { Switch } from '@components/Switch';
import creditCardService from '@services/creditCardService';
import { CARD_BRANDS, CARD_BRAND_LABELS, type Account, type CardBrand, type Category, type CreditCard, type CreditCardInput } from '@/types/finance';
import { DEFAULT_COLOR } from '@/utils/colors';
import { Required, labelCls } from './financeBits';

interface Props {
  card?: CreditCard | null;
  /** Contas ativas (conta de pagamento da fatura). */
  accounts: Account[];
  /** Todas as categorias; só as de transferência entram no select do espelho. */
  categories: Category[];
  onClose: () => void;
  onSaved: (card: CreditCard) => void;
  onError: (err: unknown) => void;
}

interface FormState {
  name: string;
  brand: CardBrand | '';
  last4: string;
  closing_day: number;
  due_day: number;
  limit_cents: number | null;
  payment_account_id: number | '';
  category_id: number | '';
  color: string;
  is_active: boolean;
}

const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

/** Cadastro/edição de cartão de crédito (POST/PUT /credit-cards). */
export const CardFormModal: React.FC<Props> = ({ card, accounts, categories, onClose, onSaved, onError }) => {
  const isEdit = !!card;
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<FormState>(() => ({
    name: card?.name ?? '',
    brand: card?.brand ?? '',
    last4: card?.last4 ?? '',
    closing_day: card?.closing_day ?? 1,
    due_day: card?.due_day ?? 10,
    limit_cents: card?.limit_cents ?? null,
    payment_account_id: card?.payment_account_id ?? '',
    category_id: card?.category_id ?? '',
    color: card?.color ?? DEFAULT_COLOR,
    is_active: card?.is_active ?? true,
  }));

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((f) => ({ ...f, [key]: value }));

  const transferCategories = useMemo(() => categories.filter((c) => c.movement === 'transfer' && (!c.is_disabled || c.id === form.category_id)), [categories, form.category_id]);
  // Em edição a conta pode estar inativa (fora da lista): mantém como opção.
  const accountOptions = useMemo(
    () => (card?.payment_account_id && !accounts.some((a) => a.id === card.payment_account_id) ? [...accounts, { id: card.payment_account_id, name: `${card.payment_account_name ?? 'Conta'} (inativa)` } as Account] : accounts),
    [accounts, card],
  );

  const sameDay = form.closing_day === form.due_day;
  const last4Ok = form.last4 === '' || /^\d{4}$/.test(form.last4);
  const canSubmit = !saving && !!form.name.trim() && !sameDay && last4Ok;

  const save = async () => {
    if (!canSubmit) return;
    const payload: CreditCardInput = {
      name: form.name.trim(),
      brand: form.brand || null,
      last4: form.last4 || null,
      closing_day: form.closing_day,
      due_day: form.due_day,
      limit_cents: form.limit_cents ?? null,
      payment_account_id: form.payment_account_id === '' ? null : form.payment_account_id,
      category_id: form.category_id === '' ? null : form.category_id,
      color: form.color,
      is_active: form.is_active,
    };
    setSaving(true);
    try {
      onSaved(card ? await creditCardService.update(card.id, payload) : await creditCardService.create(payload));
    } catch (err) {
      onError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} size="lg" closeOnBackdrop={!saving}>
      <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-2">
        <CreditCardIcon size={18} className="text-slate-500" />
        <h2 className="text-h3">{isEdit ? 'Editar cartão' : 'Novo cartão'}</h2>
      </div>
      <div className="px-6 py-4 max-h-[70vh] overflow-y-auto custom-scrollbar grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <label className={labelCls}>
            Nome
            <Required />
          </label>
          <input className="input-field" maxLength={100} value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Ex.: Nubank" autoFocus />
        </div>
        <div>
          <label className={labelCls}>Bandeira</label>
          <select className="select-field" value={form.brand} onChange={(e) => set('brand', e.target.value as CardBrand | '')}>
            <option value="">—</option>
            {CARD_BRANDS.map((b) => (
              <option key={b} value={b}>
                {CARD_BRAND_LABELS[b]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>Final (4 dígitos)</label>
          <input
            className="input-field"
            inputMode="numeric"
            maxLength={4}
            value={form.last4}
            onChange={(e) => set('last4', e.target.value.replace(/\D/g, '').slice(0, 4))}
            placeholder="1234"
          />
          {!last4Ok && <p className="text-xs text-red-600 mt-1">Informe os 4 dígitos.</p>}
        </div>
        <div>
          <label className={labelCls}>
            Dia de fechamento
            <Required />
          </label>
          <select className="select-field" value={form.closing_day} onChange={(e) => set('closing_day', Number(e.target.value))}>
            {DAYS.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
          <p className="text-small mt-1">Compra no dia do fechamento ou depois cai na fatura seguinte.</p>
        </div>
        <div>
          <label className={labelCls}>
            Dia de vencimento
            <Required />
          </label>
          <select className="select-field" value={form.due_day} onChange={(e) => set('due_day', Number(e.target.value))}>
            {DAYS.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
          {sameDay ? (
            <p className="text-xs text-red-600 mt-1">O vencimento precisa ser diferente do fechamento.</p>
          ) : (
            <p className="text-small mt-1">{form.due_day > form.closing_day ? 'No mesmo mês do fechamento.' : 'No mês seguinte ao fechamento.'}</p>
          )}
        </div>
        <div>
          <label className={labelCls}>Limite</label>
          <MoneyInput value={form.limit_cents} onChange={(v) => set('limit_cents', v)} />
        </div>
        <div>
          <label className={labelCls}>Conta de pagamento</label>
          <select className="select-field" value={form.payment_account_id} onChange={(e) => set('payment_account_id', e.target.value ? Number(e.target.value) : '')}>
            <option value="">Nenhuma (sem lançamento na conta)</option>
            {accountOptions.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
          <p className="text-small mt-1">Com conta, cada fatura vira um lançamento previsto nela (o "espelho"), no vencimento.</p>
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls}>Categoria do espelho</label>
          <select className="select-field" value={form.category_id} onChange={(e) => set('category_id', e.target.value ? Number(e.target.value) : '')}>
            <option value="">Padrão (Pagamento de cartão)</option>
            {transferCategories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
                {c.is_disabled ? ' (desativada)' : ''}
              </option>
            ))}
          </select>
          <p className="text-small mt-1">Só categorias de transferência: o pagamento da fatura não conta como despesa (as compras já contam).</p>
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls}>Cor</label>
          <ColorPicker value={form.color} onChange={(c) => set('color', c)} />
        </div>
        {isEdit && (
          <div className="sm:col-span-2">
            <Switch checked={form.is_active} onChange={(v) => set('is_active', v)} label="Cartão ativo" />
            <p className="text-small mt-1">Cartão inativo some das listas e dos seletores; faturas e itens ficam guardados.</p>
          </div>
        )}
      </div>
      <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-lg flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={save} loading={saving} disabled={!canSubmit}>
          {isEdit ? 'Salvar' : 'Cadastrar'}
        </Button>
      </div>
    </Modal>
  );
};
